// Pure inspection rules, shared by the app (validate before signing), the
// server (onInspeccionFirmada re-validates: never trust the client) and the
// backoffice (reception vs. delivery comparator).
import { catalogo, type EstadoZona, type TipoInspeccion } from './catalogo.ts';
import type { AvisosLegalesTaller, Discrepancia, Inspeccion } from './modelo.ts';

/** UI name of each type. `recepcion` is shown as "Ingreso" everywhere. */
export const nombreTipo: Record<TipoInspeccion, string> = {
  siniestro: 'Siniestro',
  recepcion: 'Ingreso',
  reingreso: 'Reingreso',
  entrega: 'Entrega',
};

/** Types whose customer accepts legal notices (a siniestro has none). */
export const tiposConAvisosLegales = ['recepcion', 'reingreso', 'entrega'] as const;
export type TipoConAvisosLegales = (typeof tiposConAvisosLegales)[number];

/**
 * Legal notices to show for a new inspection of [tipo]: the workshop's own
 * wording (config.avisosLegales[tipo]) or the default text. Empty for a
 * siniestro.
 */
export function avisosLegalesDe(
  config: { avisosLegales?: AvisosLegalesTaller | undefined } | null | undefined,
  tipo: TipoInspeccion,
): string[] {
  if (tipo === 'siniestro') return [];
  return config?.avisosLegales?.[tipo] ?? catalogo.avisosLegales;
}

/**
 * Legal notices of an existing inspection: its frozen copy, or the default
 * text when it has none (signed before the copy existed). Never the current
 * config: editing it must not change what a customer already accepted.
 */
export function avisosFirmados(i: Pick<Inspeccion, 'tipo' | 'avisosLegales'>): string[] {
  if (i.tipo === 'siniestro') return [];
  return i.avisosLegales ?? catalogo.avisosLegales;
}

/** Types whose own damage photos are evidence (the others compare against a reference). */
const conFotosPropias = new Set<TipoInspeccion>(['siniestro', 'recepcion']);

const severidad = new Map(catalogo.estadosZona.map((e) => [e.id, e.severidad]));
const requiereFoto = new Set(catalogo.estadosZona.filter((e) => e.requiereFoto).map((e) => e.id));
const zonasPrincipales = catalogo.zonas.filter((z) => !z.extra).map((z) => z.id);

export type ErrorValidacion = { codigo: string; mensaje: string; zona?: string };

/**
 * Business rules by type:
 * - siniestro: 15 zone states and ≥1 photo per ray/gol/que zone; no
 *   accessories, fuel, legal notices or reference; the signature is optional.
 * - recepcion (Ingreso): the same damage rules plus notices and signature;
 *   it may reference the siniestro it starts from.
 * - reingreso/entrega: compared against a recepcion; signature (retiro).
 */
export function validarInspeccion(i: Inspeccion): ErrorValidacion[] {
  const errores: ErrorValidacion[] = [];
  const siniestro = i.tipo === 'siniestro';
  const nombre = (id: string) => catalogo.zonas.find((z) => z.id === id)?.nombre ?? id;

  for (const id of zonasPrincipales) {
    if (!i.zonas[id]?.estado) {
      errores.push({
        codigo: 'zona_sin_estado',
        mensaje: `${nombre(id)}: falta el estado`,
        zona: id,
      });
    }
  }
  for (const [id, z] of Object.entries(i.zonas)) {
    const fotosAntes = z.fotoIds.filter((f) => i.fotos[f]?.momento === 'antes');
    if (
      z.estado &&
      requiereFoto.has(z.estado) &&
      conFotosPropias.has(i.tipo) &&
      fotosAntes.length === 0
    ) {
      errores.push({
        codigo: 'falta_foto',
        mensaje: `${nombre(id)}: falta al menos 1 foto`,
        zona: id,
      });
    }
    if (z.entrega === 'nuevo' && !z.fotoIds.some((f) => i.fotos[f]?.momento === 'despues')) {
      errores.push({
        codigo: 'falta_foto_despues',
        mensaje: `${nombre(id)}: falta la foto del daño nuevo`,
        zona: id,
      });
    }
    for (const f of z.fotoIds) {
      if (!i.fotos[f])
        errores.push({ codigo: 'foto_inexistente', mensaje: `Foto ${f} no declarada`, zona: id });
    }
  }
  const declaradas = Object.keys(i.fotos).sort();
  if (JSON.stringify(declaradas) !== JSON.stringify([...i.fotoIds].sort())) {
    errores.push({ codigo: 'foto_ids', mensaje: 'fotoIds no coincide con las fotos declaradas' });
  }
  if (siniestro && i.avisosLegales !== undefined) {
    errores.push({ codigo: 'avisos', mensaje: 'Un siniestro no lleva avisos legales' });
  }
  if (!siniestro && !i.avisosAceptados) {
    errores.push({ codigo: 'avisos', mensaje: 'El cliente no aceptó los avisos legales' });
  }
  if (i.tipo === 'entrega') {
    if (!i.firmaRetiro) errores.push({ codigo: 'firma', mensaje: 'Falta la firma de retiro' });
  } else if (!siniestro && !i.firma) {
    errores.push({ codigo: 'firma', mensaje: 'Falta la firma del cliente' });
  }
  if ((i.tipo === 'reingreso' || i.tipo === 'entrega') && !i.recepcionId) {
    errores.push({ codigo: 'recepcion', mensaje: 'Falta el ingreso de referencia' });
  }
  if (!siniestro && i.motivo === 'siniestro' && !i.aseguradora?.trim()) {
    errores.push({ codigo: 'aseguradora', mensaje: 'Siniestro sin aseguradora' });
  }
  if (i.siniestroId !== undefined && i.tipo !== 'recepcion') {
    errores.push({
      codigo: 'siniestro_no_aplica',
      mensaje: 'Solo un ingreso parte de un siniestro',
    });
  }
  if (i.seguro !== undefined && !siniestro) {
    errores.push({ codigo: 'seguro_no_aplica', mensaje: 'Los datos del seguro son del siniestro' });
  }
  if (siniestro) {
    if (Object.keys(i.accesorios).length > 0) {
      errores.push({ codigo: 'accesorios_no_aplica', mensaje: 'El siniestro no lleva accesorios' });
    }
    if (i.combustible !== null) {
      errores.push({
        codigo: 'combustible_no_aplica',
        mensaje: 'El siniestro no lleva combustible',
      });
    }
    if (i.recepcionId !== undefined || i.firmaRetiro !== undefined) {
      errores.push({ codigo: 'referencia_no_aplica', mensaje: 'El siniestro no tiene referencia' });
    }
  }
  return errores;
}

/**
 * The signed inspection this one must reference (and is compared against):
 * a reingreso/entrega its Ingreso (recepcion); an Ingreso, optionally, the
 * siniestro it started from. A siniestro references nothing.
 */
export function referenciaDe(
  i: Pick<Inspeccion, 'tipo' | 'recepcionId' | 'siniestroId'>,
): { id: string; tipo: 'recepcion' | 'siniestro' } | null {
  if (i.tipo === 'reingreso' || i.tipo === 'entrega') {
    return i.recepcionId ? { id: i.recepcionId, tipo: 'recepcion' } : null;
  }
  if (i.tipo === 'recepcion' && i.siniestroId) return { id: i.siniestroId, tipo: 'siniestro' };
  return null;
}

const sev = (e: EstadoZona | null | undefined) => (e ? (severidad.get(e) ?? null) : null);

/**
 * Compare an inspection against its reference: a delivery/re-entry against
 * its reception, or an Ingreso against the siniestro it started from. Severity order is
 * sin < ray < gol < que; `na` and unset states are not comparable. An explicit
 * `entrega: 'nuevo'` mark always counts as new damage.
 */
export function calcularDiscrepancias(
  recepcion: Inspeccion,
  posterior: Inspeccion,
): Discrepancia[] {
  const out: Discrepancia[] = [];
  const zonas = new Set([...Object.keys(recepcion.zonas), ...Object.keys(posterior.zonas)]);
  for (const zona of zonas) {
    const antes = recepcion.zonas[zona]?.estado ?? null;
    const despues = posterior.zonas[zona]?.estado ?? null;
    const marca = posterior.zonas[zona]?.entrega;
    const a = sev(antes);
    const d = sev(despues);
    if (marca === 'nuevo') {
      out.push({ zona, antes, despues, tipo: 'dano_nuevo' });
    } else if (a !== null && d !== null && d > a) {
      out.push({ zona, antes, despues, tipo: a === 0 ? 'dano_nuevo' : 'empeora' });
    } else if (marca === 'reparado' || (a !== null && d !== null && d < a)) {
      out.push({ zona, antes, despues, tipo: 'reparado' });
    }
  }
  return out.sort((x, y) => x.zona.localeCompare(y.zona));
}
